import { isDefined } from 'notable-common';
import { z } from 'zod';
import { FlowFhirEndpoint } from './enums.js';
import { FlowLookbackDurationSchema, FlowLookbackDurationUnit, } from '../../../duration/duration.js';
import { FlowProducedType } from '../../../flowRun/producedType.js';
import { BaseFlowBlockSchema, FlowBlockReturnTypeSchema } from '../block.js';
import { FlowConsumerOfPatientSchema, FlowConsumerOfProviderSchema } from '../consumer.js';
import { FlowBlockType } from '../type.js';
const BaseFhirBlockConfigSchema = z.object({
    endpoint: z.enum(FlowFhirEndpoint),
    patient: FlowConsumerOfPatientSchema({ isRequired: true }),
});
const GetPatientConfigSchema = BaseFhirBlockConfigSchema.omit({ patient: true }).extend({
    endpoint: z.literal(FlowFhirEndpoint.GetPatient),
    patient: FlowConsumerOfPatientSchema({ isRequired: false }).optional(),
    patientSearchFilters: z
        .object({
        lastName: z.string().min(1, 'Last name is required'),
        // eslint-disable-next-line no-restricted-syntax -- the Date branch predates the rule; collapsing it to the string branch changes the inferred type that the endpoint builder and its tests rely on
        dateOfBirth: z.date().or(z.string().min(1, 'Date of birth is required')),
        phoneNumber: z.string().optional(),
        ehrId: z.string().optional(),
        firstName: z.string().min(1, 'First name is required'),
        email: z.string().optional(),
        patientId: z.string().optional(),
        zipCode: z.string().optional(),
        patientEhrIdToMatch: z.string().optional(),
    })
        .optional()
        .default({
        lastName: '',
        dateOfBirth: '',
        firstName: '',
    }),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.FhirPatientResponse),
});
const PractitionerSearchConfigSchema = BaseFhirBlockConfigSchema.omit({ patient: true }).extend({
    endpoint: z.literal(FlowFhirEndpoint.PractitionerSearch),
    practitionerFilters: z
        .object({
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        npi: z.string().optional(),
        provider: FlowConsumerOfProviderSchema.optional(),
    }, { message: 'Practitioner information is required' })
        .optional()
        .default({})
        .superRefine((data, context) => {
        if (!isDefined(data.provider) && !isDefined(data.npi)) {
            context.addIssue({
                code: 'custom',
                message: 'Either provider or npi must be set for practitioner search',
                path: ['npi'],
            });
        }
    }),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.Practitioner),
});
const ListEncountersConfigSchema = BaseFhirBlockConfigSchema.extend({
    endpoint: z.literal(FlowFhirEndpoint.ListEncounters),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.Encounters),
});
const BookedAppointmentsConfigSchema = BaseFhirBlockConfigSchema.extend({
    endpoint: z.literal(FlowFhirEndpoint.BookedAppointments),
    appointmentFilters: z
        .object({
        after: FlowLookbackDurationSchema,
    })
        .optional()
        .default({
        after: {
            value: -1,
            unit: FlowLookbackDurationUnit.Days,
        },
    }),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.Appointments),
});
const LabValuesConfigSchema = BaseFhirBlockConfigSchema.extend({
    endpoint: z.literal(FlowFhirEndpoint.LabValues),
    labValueFilters: z
        .object({
        categories: z.array(z.string()).optional(),
    })
        .optional(),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.LabValues),
});
const ServiceRequestsConfigSchema = BaseFhirBlockConfigSchema.extend({
    endpoint: z.literal(FlowFhirEndpoint.ServiceRequests),
    serviceRequestFilters: z
        .object({
        providerNpi: z.string().optional(),
        providerId: z.string().optional(),
        encounterId: z.string().optional(),
    })
        .optional(),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.ServiceRequests),
});
const MedicationsConfigSchema = BaseFhirBlockConfigSchema.extend({
    endpoint: z.literal(FlowFhirEndpoint.Medications),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.Medications),
});
const ConditionsConfigSchema = BaseFhirBlockConfigSchema.extend({
    endpoint: z.literal(FlowFhirEndpoint.Conditions),
    conditionFilters: z
        .object({
        verificationStatuses: z.array(z.string()).optional(),
    })
        .optional(),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.Conditions),
});
const CoveragesConfigSchema = BaseFhirBlockConfigSchema.extend({
    endpoint: z.literal(FlowFhirEndpoint.Coverages),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.Coverages),
});
const DocumentsConfigSchema = BaseFhirBlockConfigSchema.extend({
    endpoint: z.literal(FlowFhirEndpoint.Documents),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.DocumentMetadata),
});
const BinariesConfigSchema = BaseFhirBlockConfigSchema.extend({
    endpoint: z.literal(FlowFhirEndpoint.Binaries),
    binaryFilters: z
        .object({
        bucketName: z
            .string({ message: 'Bucket name is required' })
            .min(1, 'Bucket name is required'),
        pathPrefix: z
            .string({ message: 'Path prefix is required' })
            .min(1, 'Path prefix is required'),
        binaryUrls: z.array(z.string().min(1, { message: 'Binary URL is required' }), {
            message: 'Binary URLs are required',
        }),
    })
        .optional()
        .default({
        bucketName: '',
        pathPrefix: '',
        binaryUrls: [],
    }),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.DocumentBinaries),
});
const SurgicalHistoriesConfigSchema = BaseFhirBlockConfigSchema.extend({
    endpoint: z.literal(FlowFhirEndpoint.SurgicalHistories),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.SurgicalHistories),
});
export const FlowFhirBlockConfigSchema = z.discriminatedUnion('endpoint', [
    GetPatientConfigSchema,
    PractitionerSearchConfigSchema,
    ListEncountersConfigSchema,
    BookedAppointmentsConfigSchema,
    LabValuesConfigSchema,
    ServiceRequestsConfigSchema,
    MedicationsConfigSchema,
    ConditionsConfigSchema,
    DocumentsConfigSchema,
    BinariesConfigSchema,
    SurgicalHistoriesConfigSchema,
    CoveragesConfigSchema,
]);
export const FlowFhirBlockSchema = BaseFlowBlockSchema.extend({
    type: z.literal(FlowBlockType.Fhir),
    config: FlowFhirBlockConfigSchema,
});
//# sourceMappingURL=config.js.map